import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { setCanonical, setMeta } from '@/utils/seo';
import { BreadcrumbSchema, SpeakableSchema, ArticleSchema } from '@/components/SEOSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    setCanonical('/blog');
    setMeta('Blog | LegaSecure Guide', 'Latest articles on estate planning, SPA, guardian mandates, and legacy protection for Filipinos and OFWs.');
    axios.get(`${API}/blog?limit=50`).then(r => setPosts(r.data.posts || [])).catch(() => {});
    axios.get(`${API}/guides`).then(r => setGuides(r.data.guides || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="blog-page">
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Blog' }]} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>LegaSecure Guide Blog</h1>
          <p className="speakable-intro text-lg" style={{ color: '#4A5568' }}>Latest articles on estate planning, SPA, and legacy protection for Filipinos and OFWs.</p>
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              {posts.length === 0 ? (
                <div className="ls-card text-center py-12">
                  <i className="fa-solid fa-newspaper text-4xl text-[var(--ls-text-light)] mb-4"></i>
                  <h3 className="text-lg font-bold mb-2">Coming Soon</h3>
                  <p className="text-[var(--ls-text-light)]">Blog posts are being generated. Check back soon!</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {posts.map(post => (
                    <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`} className="ls-card block group">
                      <h2 className="text-xl font-bold mb-2 group-hover:text-[var(--ls-gold)] transition-colors">{post.title}</h2>
                      <p className="text-sm text-[var(--ls-text-light)] mb-3 line-clamp-2">{post.excerpt}</p>
                      <div className="flex justify-between items-center text-xs text-[var(--ls-text-light)]">
                        <span>{post.author}</span>
                        <span>{new Date(post.published_at).toLocaleDateString()}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="ls-card mb-6">
                <h3 className="font-bold mb-4 text-[var(--ls-navy)]">Legal Guides</h3>
                <div className="space-y-3">
                  {guides.map(g => (
                    <Link key={g.slug} to={`/guides/${g.slug}`} className="block text-sm text-[var(--ls-text)] hover:text-[var(--ls-gold)] transition-colors">
                      <i className="fa-solid fa-file-lines mr-2 text-[var(--ls-gold)]"></i>{g.title}
                    </Link>
                  ))}
                </div>
              </div>
              <a href="https://legasecure.com" className="ls-card block bg-[var(--ls-navy)] text-center">
                <i className="fa-solid fa-shield-halved text-[var(--ls-gold)] text-2xl mb-2"></i>
                <p className="text-white font-bold mb-1">Protect Your Legacy</p>
                <p className="text-white/70 text-sm mb-3">Start your estate plan today</p>
                <span className="ls-btn-gold text-sm">Get Started Free</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/${slug}`).then(r => {
      setPost(r.data);
      setCanonical(`/blog/${slug}`);
      setMeta(r.data.meta_title, r.data.meta_description);
    }).catch(() => setPost(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="ls-container py-20 text-center"><i className="fa-solid fa-spinner fa-spin text-2xl text-[var(--ls-navy)]"></i></div>;
  if (!post) return <div className="ls-container py-20 text-center"><h1 className="text-2xl font-bold">Post not found</h1></div>;

  const renderContent = (text) => {
    const convertLinks = (str) => {
      return str
        .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" class="text-[var(--ls-gold-dark)] underline hover:text-[var(--ls-gold)]">$1</a>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    };
    
    return text.split('\n').map((line, i) => {
      if (line.startsWith('# ') && !line.startsWith('## ') && !line.startsWith('### ')) return <h2 key={i} className="text-xl font-bold mt-6 mb-3">{line.replace('# ', '')}</h2>;
      if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-bold mt-6 mb-3">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-bold mt-4 mb-2">{line.replace('### ', '')}</h3>;
      if (line.startsWith('- ') || line.startsWith('* ')) {
        const content = convertLinks(line.replace(/^[-*] /, ''));
        return <li key={i} className="ml-6 mb-1 list-disc" dangerouslySetInnerHTML={{ __html: content }} />;
      }
      if (line.match(/^\d+\.\s/)) {
        const content = convertLinks(line.replace(/^\d+\.\s/, ''));
        return <li key={i} className="ml-6 mb-1 list-decimal" dangerouslySetInnerHTML={{ __html: content }} />;
      }
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="mb-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: convertLinks(line) }} />;
    });
  };

  return (
    <div data-testid="blog-post-page">
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Blog', url: '/blog' }, { name: post.title }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <ArticleSchema title={post.title} description={post.meta_description} url={`/blog/${slug}`} datePublished={post.published_at} author={post.author} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <nav className="text-sm text-[var(--ls-text-light)] mb-4">
            <Link to="/" className="hover:text-[var(--ls-gold)]">Home</Link> / <Link to="/blog" className="hover:text-[var(--ls-gold)]">Blog</Link> / <span className="text-[var(--ls-navy)] font-medium">{post.title}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>{post.title}</h1>
          <div className="text-sm" style={{ color: '#4A5568' }}>{post.author} | {new Date(post.published_at).toLocaleDateString()}</div>
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container max-w-4xl">
          <div className="ls-card prose-content">{renderContent(post.content)}</div>
        </div>
      </section>
    </div>
  );
}
